/**
 * A fake server in the browser, so every screen can be built and tested without the Worker.
 * Open any link with ?mock=1 (and &speed=5 to run the clock five times faster). It creates the
 * tournament with the 32 players, starts the rounds, scores every court but the one this screen
 * is for at random moments, switches to Mexicano after six rounds and turns random events on.
 * Everyone who is here has a nickname and a stand-in face; the bench is busy (a heckle and a few
 * people's picks a round, a curse every third round (the three kinds in turn), a twist fired in
 * every fourth, a new name after a thrashing; &curses=1 throws all three in round 1 at once). Add &join=1 to a
 * player link to walk the join flow as the first player. An admin link is the owner's, with Piri
 * and Kedar as named admins; add &as=Piri to see the admin screen as that named admin.
 * It enforces the same role permissions as the real server.
 *
 * PINs (decisions 39 and 40): every pretend player who has joined, the owner, Piri and Kedar have
 * the PIN 1234; the &join=1 player has none yet. A phone that picks a name types 1234 (or sets
 * one for the &join=1 player). The admin screen opens as if this phone had typed its admin PIN
 * already; add &lock=1 to see it ask for the PIN (1234), or &lock=new to see it ask for a new one.
 * Sessions are kept in the phone like the real ones, and a session made on an earlier page of the
 * pretend day is taken as good unless its PIN was reset on this page.
 *
 * Rounds wait for an admin's Start, as a new day does (decision 41). On the admin screen that
 * admin is you; on every other screen a pretend admin starts the next round once every court is
 * ready, or a changeover after the clock if one is not. &auto=1 makes rounds start by themselves.
 * &at=13:05 starts the pretend day at that time today, with the real slot (13 and 2) and the last
 * whistle at 17:45, so pictures show the hall's times and sums. &before=1 holds the day before
 * round 1 (the pretend day otherwise starts round 1 at once): on the owner's admin screen that
 * shows the Slides card, whose slides only the owner's phone moves (&as=Piri does not see it).
 * &stopped=1 replaces this screen's link two seconds in, to see what a screen on an old link shows.
 */

import { dueActions, nextAlarm } from "../../../engine/autopilot";
import { CURSE_KINDS, HECKLES, pickProblem } from "../../../engine/bench";
import { briefPlayers } from "../../../engine/players";
import { makeRng } from "../../../engine/rng";
import { eventById, isFinished, rulesFor } from "../../../engine/scoring";
import { courtsReady, currentRound } from "../../../engine/selectors";
import { gamesDone } from "../../../engine/winddown";
import { MINUTE, apply, emptyState, retired } from "../../../engine/state";
import type { LoggedEvent, Round, Score, TournamentEvent, TournamentState } from "../../../engine/types";
import { addAdmin, adminFor, cleanName, linksFor, removeAdmin, renewAdmin, type NamedAdmin } from "../../../shared/admins";
import { LOCKED, MemoryPinStore, OWNER, PinDesk, type PinKind } from "../../../shared/pins";
import { allowed, signedBy, type Answer, type ClientMessage, type Identity, type Links, type ServerMessage } from "../../../shared/protocol";
import { standInFace } from "./photos";
import type { Status, Transport } from "./transport";

/** Nicknames for the pretend day, dealt out in order. */
// The pretend day shows real first names only: made-up nicknames read like team names in pictures.

/** The pretend PIN, 1234, as the desk keeps it (salted SHA-256 of it with the salt "mock"). */
const MOCK_PIN = { hash: "d5430704d7cff29266a710e3691fe3b2b08881eb39f5a046c5b039bfb0632edf", salt: "mock" };

/**
 * The pretend day's PINs in memory. A session made on an earlier page of the pretend day (this
 * page started a fresh pretend server) is taken as good, unless its PIN was reset on this page.
 */
class MockPinStore extends MemoryPinStore {
  private readonly startedAt = Date.now();
  private revoked = new Set<string>();

  override session(token: string): { kind: PinKind; id: string } | null {
    const kept = super.session(token);
    if (kept) return kept;
    const [mock, kind, id, at] = token.split("~");
    if (mock !== "mock" || (kind !== "player" && kind !== "admin") || !id || !(Number(at) < this.startedAt)) return null;
    return this.revoked.has(`${kind}~${id}`) ? null : { kind, id };
  }

  override dropSessions(kind: PinKind, id: string): void {
    super.dropSessions(kind, id);
    this.revoked.add(`${kind}~${id}`);
  }

  override wipe(kind: PinKind): void {
    super.wipe(kind);
    this.revoked.add(`${kind}~*`);
  }
}

export interface MockOptions {
  identity: Identity;
  /** The token in the page's address: an admin page opened with a named admin's token is that admin. */
  token?: string;
  /** How many fake seconds pass per real second. */
  speed: number;
  /** Rounds as short as this, so screens cycle quickly: game and changeover in ms. */
  gameMs?: number;
  changeoverMs?: number;
}

/** "13:05" as that time today, in ms; null for anything else. */
function dayAt(hhmm: string | null): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm ?? "");
  if (!m) return null;
  const d = new Date();
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return d.getTime();
}

export class MockTransport implements Transport {
  private state: TournamentState = emptyState();
  private events: LoggedEvent[] = [];
  private onMessage: ((m: ServerMessage) => void) | null = null;
  private onStatus: ((s: Status) => void) | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly rng = makeRng(99);
  private readonly started = Date.now();
  /** &at=13:05: the pretend day starts at that time today (pictures for the docs show hall times). */
  private readonly base = dayAt(new URLSearchParams(location.search).get("at")) ?? this.started;
  private seen = new Set<string>();
  private scoreAt = new Map<string, number>();
  /** Pictures taken during this pretend day, by player. */
  private photos = new Map<string, string>();
  private benchRound = 0;
  /** When the day last went between rounds, for the pretend admin's Start. */
  private idleSince: number | null = null;
  private links: Omit<Links, "admins"> = {
    projector: "/p/mock-projector?mock=1",
    calibration: "/p/mock-projector/cal?mock=1",
    courts: [1, 2, 3, 4].map((n) => `/c/mock-court/${n}?mock=1`),
    admin: "/a/mock-admin?mock=1",
    players: "/j/mock-players?mock=1",
    watcher: "/w/mock-watcher?mock=1",
    remote: "/api/remote/mock-remote?mock=1",
  };
  /**
   * Two helpers with links of their own, so the Admins fold has something in it (decision 38).
   * Kedar was added by Piri, so a picture shows both ways a row says who made it.
   */
  private admins: NamedAdmin[] = ["Piri", "Kedar"].map((name, i) => ({ id: `m${i + 1}`, name, token: `mock-admin-m${i + 1}`, addedAt: this.started, addedBy: i ? "admin:Piri" : "owner" }));
  private madeLinks = 0;
  /**
   * Who this screen is. An admin page is the owner's, or with &as=Name (or a named admin's own
   * token in the address) that named admin's. A player's phone is a player once its PIN is in.
   */
  private identity: Identity;
  /** The named admin this admin page is, or null for the owner. */
  private readonly adminId: string | null = null;
  private readonly pinStore = new MockPinStore();
  private readonly desk = new PinDesk(this.pinStore, (kind, id) => `mock~${kind}~${id}~${Date.now()}~${Math.random().toString(36).slice(2, 8)}`);
  /** This phone's player session, as the real server keeps it on the socket. */
  private session: string | null = null;

  constructor(private readonly opts: MockOptions) {
    const q = new URLSearchParams(location.search);
    const as = opts.identity.role === "admin" ? cleanName(q.get("as") ?? "") : "";
    if (as && !this.admins.some((a) => a.name.toLowerCase() === as.toLowerCase())) {
      this.admins.push({ id: "m-as", name: as, token: "mock-admin-as", addedAt: this.started, addedBy: "owner" });
    }
    const byToken = opts.token ? adminFor({ projector: "", courts: [], admin: "mock-admin", players: "", admins: this.admins }, opts.token) : null;
    const named = this.admins.find((a) => as && a.name.toLowerCase() === as.toLowerCase()) ?? (byToken && !byToken.owner ? byToken.admin : undefined);
    this.adminId = named?.id ?? null;
    // the admin screen opens as if its PIN had been typed, unless &lock= asks to see the lock
    const lock = q.get("lock");
    this.identity =
      opts.identity.role !== "admin"
        ? opts.identity
        : { ...opts.identity, owner: !named, ...(named ? { name: named.name } : {}), locked: lock !== null, pinSet: lock !== "new" };
    for (const a of this.admins) this.pinStore.setPin("admin", a.id, { ...MOCK_PIN, setAt: this.started });
    if (lock !== "new") this.pinStore.setPin("admin", OWNER, { ...MOCK_PIN, setAt: this.started });
    else if (named) this.pinStore.setPin("admin", named.id, null);
  }

  /** The admin this page is, in the PIN tables. */
  private get adminKey(): string {
    return this.adminId ?? OWNER;
  }

  /** The links this screen would be sent by the real server. */
  private linksNow(): Links | null {
    const admins = this.admins.map((a) => ({ id: a.id, name: a.name, link: `/a/${a.token}?mock=1`, addedBy: a.addedBy }));
    return linksFor({ ...this.links, admins }, this.identity);
  }

  private sendLinks(): void {
    const links = this.linksNow();
    if (links) this.onMessage?.({ kind: "links", links });
  }

  /** Fake time: runs `speed` times faster than the wall clock, from when the page loaded. */
  now(): number {
    return this.base + (Date.now() - this.started) * this.opts.speed;
  }

  connect(onMessage: (m: ServerMessage) => void, onStatus: (s: Status) => void): void {
    this.onMessage = onMessage;
    this.onStatus = onStatus;
    if (!this.events.length) this.bootstrap();
    setTimeout(() => onStatus("open"), 50);
    if (!this.timer) this.timer = setInterval(() => this.tick(), 250);
    // &stopped=1: two seconds in, this screen's link is replaced, as the real server says it
    if (new URLSearchParams(location.search).get("stopped") === "1") {
      setTimeout(() => this.onMessage?.({ kind: "error", reason: "This link has been replaced. Ask an admin for the new one." }), 2000);
    }
  }

  send(m: ClientMessage): boolean {
    setTimeout(() => this.handle(m), 30);
    return true;
  }

  async uploadPhoto(playerId: string, jpeg: Blob, session: string | null): Promise<string | null> {
    const player = this.state.players[playerId];
    if (!player) return "No such player";
    if (this.desk.check(session, "player") !== playerId) return "Type your PIN first.";
    this.photos.set(playerId, URL.createObjectURL(jpeg));
    this.append({ type: "player.profile", id: playerId, photo: player.photo + 1 }, "server");
    return null;
  }

  photoUrl(playerId: string): string {
    return this.photos.get(playerId) ?? standInFace(playerId);
  }

  close(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.onStatus?.("offline");
  }

  /** The pretend server is in the page: its line never goes quiet, so there is nothing to start again. */
  restart(): void {}

  private append(event: TournamentEvent, by: string): LoggedEvent {
    const logged = { ...event, seq: this.state.seq + 1, at: this.now(), by } as LoggedEvent;
    this.state = apply(this.state, logged);
    this.events.push(logged);
    this.onMessage?.({ kind: "events", events: [logged], serverNow: this.now() });
    return logged;
  }

  private bootstrap(): void {
    const players = briefPlayers();
    const walkJoin = new URLSearchParams(location.search).get("join") === "1";
    this.append(
      {
        type: "tournament.created",
        config: {
          // with &at, the real day's slot too, so the wall's sums read as they will on the day
          gameMs: this.base !== this.started ? 13 * MINUTE : (this.opts.gameMs ?? 13 * MINUTE),
          changeoverMs: this.base !== this.started ? 2 * MINUTE : (this.opts.changeoverMs ?? 2 * MINUTE),
          // with &at, the real day's last whistle (17:45); otherwise five hours from the start
          lastWhistleAt: this.base !== this.started ? (dayAt("17:45") as number) : this.now() + 5 * 60 * MINUTE,
          autoAdvance: new URLSearchParams(location.search).get("auto") === "1",
        } as never,
        players,
        seed: 42,
      },
      "admin",
    );
    players.slice(0, 29).forEach((p, i) => {
      this.append({ type: "player.status", id: p.id, status: "in" }, p.id);
      // with &join=1 the first one keeps a bare profile and no PIN, so the join flow can be walked through as them
      if (i > 0 || !walkJoin) {
        this.append({ type: "player.profile", id: p.id, nickname: null, photo: 1 }, "server");
        this.pinStore.setPin("player", p.id, { ...MOCK_PIN, setAt: this.now() });
      }
    });
    // someone has been trying a name that is not theirs, so the admin's Players tab has a row to show it
    const tried = players[3];
    if (tried) this.pinStore.setGuard("player", tried.id, { streak: 3, total: 3, lastAt: Date.now() - 4 * MINUTE, lockedUntil: 0 });
    for (const ev of dueActions(this.state, this.now())) this.append(ev, "server");
    // &before=1: the day before round 1 (the join wall, and the owner's Slides card on the admin screen)
    if (new URLSearchParams(location.search).get("before") === "1") return;
    if (this.state.draft) this.append({ type: "round.started", lineup: this.state.draft }, "admin");
    // for pictures: &twist=1 starts a twist in round 1 at once; &off=curses,heckles,minigames
    // turns the Events tab's switches off (after the twist, so &twist=1&off=minigames shows it ended)
    const q = new URLSearchParams(location.search);
    if (q.get("twist") === "1" && this.state.clock.phase === "playing" && this.state.clock.roundNo !== null) {
      this.attempt({ type: "event.fired", roundNo: this.state.clock.roundNo, eventId: "offhand", courts: "all" }, "admin");
    }
    // &curses=1 throws all three curses in round 1 at once, on courts 1 to 3, each on the top pair:
    // wrong hand on court 1, no smashes on court 2, solo on court 3
    if (q.get("curses") === "1" && this.state.clock.phase === "playing" && this.state.clock.roundNo !== null) {
      const resting = currentRound(this.state)?.lineup.sittingOut ?? [];
      CURSE_KINDS.forEach((kind, i) => {
        const by = resting[i];
        if (by) this.attempt({ type: "curse.thrown", player: by, court: i + 1, side: "a", kind }, by);
      });
    }
    const off = (q.get("off") ?? "").split(",");
    if (off.includes("curses")) this.append({ type: "bench.curses", on: false }, "admin");
    if (off.includes("heckles")) this.append({ type: "bench.muted", on: true }, "admin");
    if (off.includes("minigames")) this.append({ type: "minigames.set", on: false }, "admin");
  }

  private handle(m: ClientMessage): void {
    switch (m.kind) {
      case "hello": {
        let said: "ok" | "gone" | undefined;
        if (m.session !== undefined) {
          const kind: PinKind = this.identity.role === "admin" ? "admin" : "player";
          const id = this.desk.check(m.session, kind, kind === "admin" ? this.adminKey : undefined);
          said = id && (kind === "admin" || this.state.players[id]) ? "ok" : "gone";
          if (said === "ok" && kind === "player" && id) {
            this.identity = { ...this.identity, player: id };
            this.session = m.session;
          }
          // &lock= is there to show the lock: a session kept from an earlier page does not open it
          if (said === "ok" && kind === "admin" && new URLSearchParams(location.search).get("lock") === null) this.identity = { ...this.identity, locked: false };
        }
        this.onMessage?.({ kind: "welcome", identity: this.identity, serverNow: this.now(), state: this.state, ...(said ? { session: said } : {}) });
        if (this.identity.role === "admin" && this.identity.locked) break;
        this.sendLinks();
        if (this.identity.role === "admin") {
          this.onMessage?.({ kind: "pins", book: this.desk.book(Date.now(), this.admins.map((a) => a.id)) });
          // the pretend hall: everything connected but court 3's tablet, gone two minutes ago, and no watcher
          const screens = { projector: 1, courts: [1, 1, 0, 1], admin: 1, players: 14, watcher: 0, gone: { "court-3": this.now() - 2 * MINUTE } };
          this.onMessage?.({ kind: "screens", screens, serverNow: this.now() });
        }
        break;
      }
      case "reset": {
        if (this.identity.role !== "admin" || m.confirm !== "RESET" || this.identity.locked) break;
        this.desk.wipe("player");
        this.state = emptyState();
        this.events = [];
        this.seen.clear();
        this.scoreAt.clear();
        this.photos.clear();
        this.benchRound = 0;
        this.bootstrap();
        this.onMessage?.({ kind: "welcome", identity: this.identity, serverNow: this.now(), state: this.state });
        break;
      }
      case "append": {
        if (this.identity.role === "admin" && this.identity.locked) {
          this.onMessage?.({ kind: "refused", id: m.id, reason: "Type your admin PIN first.", serverNow: this.now() });
          break;
        }
        if (this.seen.has(m.id)) {
          this.onMessage?.({ kind: "ack", id: m.id, seq: this.state.seq, serverNow: this.now() });
          break;
        }
        const why = allowed(this.identity, m.event) ?? retired(this.state, m.event);
        if (why) {
          this.onMessage?.({ kind: "refused", id: m.id, reason: why, serverNow: this.now() });
          break;
        }
        try {
          // as on the real server, the link decides who an action is logged as
          const logged = this.append(m.event, signedBy(this.identity, m.by, m.event));
          this.seen.add(m.id);
          this.onMessage?.({ kind: "ack", id: m.id, seq: logged.seq, serverNow: this.now() });
          this.runAutopilot();
        } catch (err) {
          this.onMessage?.({ kind: "refused", id: m.id, reason: (err as Error).message, serverNow: this.now() });
        }
        break;
      }
      case "log":
        this.onMessage?.({ kind: "log", events: this.events.slice(-Math.max(1, Math.min(200, m.last))) });
        break;
      case "regenerate":
        // decision 51: the wall, courts, players and watcher keep their links all day
        if (m.role !== "admin" && m.role !== "remote") {
          this.onMessage?.({ kind: "error", reason: "That link stays the same all day. Only the owner link and the watch remote can be replaced." });
          break;
        }
        if (m.role === "admin" && this.identity.owner === false) {
          this.onMessage?.({ kind: "error", reason: "Only the owner can replace the owner's link." });
          break;
        }
        this.sendLinks();
        break;
      case "links":
        this.sendLinks();
        break;
      case "admins.add": {
        if (this.identity.role !== "admin") break;
        this.madeLinks += 1;
        const added = addAdmin(this.admins, m.name, { id: `m-new-${this.madeLinks}`, token: `mock-admin-new-${this.madeLinks}`, at: this.now(), by: this.identity.owner === false ? `admin:${this.identity.name ?? ""}` : "owner" });
        if ("reason" in added) this.onMessage?.({ kind: "error", reason: added.reason });
        else this.admins = added.list;
        this.sendLinks();
        break;
      }
      case "admins.remove":
      case "admins.renew": {
        if (this.identity.role !== "admin") break;
        this.madeLinks += 1;
        const changed = m.kind === "admins.remove" ? removeAdmin(this.admins, m.id) : renewAdmin(this.admins, m.id, `mock-admin-renewed-${this.madeLinks}`);
        if (!changed) this.onMessage?.({ kind: "error", reason: "That admin is not on the list any more." });
        else this.admins = changed.list;
        this.sendLinks();
        break;
      }
      case "ping":
        this.onMessage?.({ kind: "pong", t: m.t, serverNow: this.now() });
        break;
      case "pin.check":
      case "pin.claim":
      case "session.end":
      case "admin.pin":
      case "admin.names":
      case "admin.open":
      case "pin.reset":
        void this.pins(m);
        break;
      default:
        break;
    }
  }

  private answer(rid: string, body: Omit<Answer, "kind" | "rid">): void {
    this.onMessage?.({ kind: "answer", rid, ...body });
  }

  private tellPins(news?: string): void {
    if (this.identity.role !== "admin" || this.identity.locked) return;
    this.onMessage?.({ kind: "pins", book: this.desk.book(Date.now(), this.admins.map((a) => a.id)), ...(news ? { news } : {}) });
  }

  /** The PIN messages, as the real server answers them (worker/src/tournament.ts). */
  private async pins(m: ClientMessage): Promise<void> {
    const now = Date.now();
    switch (m.kind) {
      case "pin.check": {
        if (this.identity.role !== "player") return;
        if (!this.state.players[m.player]) return this.answer(m.rid, { ok: false, reason: "That name is not on the list any more." });
        const set = this.desk.isSet("player", m.player);
        return this.answer(m.rid, { ok: true, set, lockedUntil: this.desk.lockedUntil("player", m.player, now) ?? (set ? this.desk.claimPausedUntil(now) : null) });
      }
      case "pin.claim": {
        if (this.identity.role !== "player") return;
        const player = this.state.players[m.player];
        if (!player) return this.answer(m.rid, { ok: false, reason: "That name is not on the list any more." });
        const paused = m.mode === "set" ? null : this.desk.claimPausedUntil(now);
        if (paused !== null) return this.answer(m.rid, { ok: false, reason: LOCKED, set: this.desk.isSet("player", m.player), lockedUntil: paused });
        const result = await this.desk.enter("player", m.player, m.pin, m.mode === "set" ? "set" : "enter", now, player.name);
        if (!result.ok) {
          if (result.wrong) this.desk.claimWrong(now);
          return this.answer(m.rid, { ok: false, reason: result.reason, set: result.set, lockedUntil: result.lockedUntil });
        }
        if (this.session && this.session !== result.session) this.desk.end(this.session);
        this.session = result.session;
        this.identity = { ...this.identity, player: m.player };
        this.onMessage?.({ kind: "session", as: "player", session: result.session, player: m.player });
        this.onMessage?.({ kind: "identity", identity: this.identity });
        return this.answer(m.rid, { ok: true });
      }
      case "session.end": {
        if (this.session) this.desk.end(this.session);
        this.session = null;
        const { player: _gone, ...rest } = this.identity;
        this.identity = rest;
        this.onMessage?.({ kind: "identity", identity: this.identity });
        return;
      }
      case "admin.pin": {
        if (this.identity.role !== "admin") return;
        const label = this.adminId ? this.identity.name ?? "That admin" : "the owner";
        const result = await this.desk.enter("admin", this.adminKey, m.pin, m.mode === "set" ? "set" : "enter", now, label);
        if (!result.ok) {
          this.answer(m.rid, { ok: false, reason: result.reason, set: result.set, lockedUntil: result.lockedUntil });
          return;
        }
        this.identity = { ...this.identity, locked: false, pinSet: true };
        const self = this.admins.find((a) => a.id === this.adminId);
        this.onMessage?.({ kind: "session", as: "admin", session: result.session, token: self ? self.token : this.opts.token ?? "mock-admin", name: self ? self.name : null, owner: !self });
        this.onMessage?.({ kind: "identity", identity: this.identity });
        this.sendLinks();
        this.answer(m.rid, { ok: true });
        this.tellPins();
        return;
      }
      case "admin.names": {
        if (this.identity.role !== "player") return;
        const admins = this.admins.filter((a) => this.desk.isSet("admin", a.id)).map((a) => ({ id: a.id, name: a.name }));
        return this.answer(m.rid, { ok: true, admins, pausedUntil: this.desk.pickPausedUntil(now) });
      }
      case "admin.open": {
        if (this.identity.role !== "player") return;
        const viaLink = typeof m.link === "string" && m.link !== "";
        // in the pretend day any admin page that is not a named admin's is the owner's
        const found = viaLink ? adminFor({ projector: "", courts: [], admin: "mock-admin", players: "", admins: this.admins }, m.link as string) ?? { owner: true as const } : null;
        const named = viaLink ? (found && !found.owner ? found.admin : null) : this.admins.find((a) => a.id === m.admin) ?? null;
        if (viaLink ? !found : !named || !this.desk.isSet("admin", named.id)) return this.answer(m.rid, { ok: false, reason: "That admin is not on the list any more." });
        const id = named ? named.id : OWNER;
        const paused = this.desk.pickPausedUntil(now);
        if (!viaLink && paused !== null) return this.answer(m.rid, { ok: false, reason: "Too many wrong admin PINs.", lockedUntil: paused });
        if (!this.desk.isSet("admin", id)) return this.answer(m.rid, { ok: false, set: false, reason: "No admin PIN yet." });
        const result = await this.desk.enter("admin", id, m.pin, "enter", now, named ? named.name : "the owner");
        if (!result.ok) {
          if (!viaLink && result.wrong) this.desk.pickWrong(now);
          return this.answer(m.rid, { ok: false, reason: result.reason, set: result.set, lockedUntil: result.lockedUntil });
        }
        const token = named ? named.token : "mock-admin";
        this.onMessage?.({ kind: "session", as: "admin", session: result.session, token, name: named ? named.name : null, owner: !named });
        return this.answer(m.rid, { ok: true, token });
      }
      case "pin.reset": {
        if (this.identity.role !== "admin" || this.identity.locked) return this.answer(m.rid, { ok: false, reason: "Type your admin PIN first." });
        if (m.as === "player") {
          if (!this.state.players[m.id]) return this.answer(m.rid, { ok: false, reason: "That player is not on the list any more." });
          this.desk.reset("player", m.id);
          this.answer(m.rid, { ok: true });
          this.tellPins();
          return;
        }
        if (m.id === OWNER ? this.adminId !== null : !this.admins.some((a) => a.id === m.id)) {
          return this.answer(m.rid, { ok: false, reason: m.id === OWNER ? "Only the owner can reset the owner's PIN." : "That admin is not on the list any more." });
        }
        this.desk.reset("admin", m.id);
        this.answer(m.rid, { ok: true });
        if (m.id === this.adminKey) {
          this.identity = { ...this.identity, locked: true, pinSet: false };
          this.onMessage?.({ kind: "signed.out", as: "admin", reason: "Your admin PIN was reset. Set a new one." });
          this.onMessage?.({ kind: "identity", identity: this.identity });
        }
        this.tellPins();
        return;
      }
      default:
        return;
    }
  }

  private runAutopilot(): void {
    for (let guard = 0; guard < 20; guard += 1) {
      const due = dueActions(this.state, this.now());
      if (!due.length) return;
      for (const ev of due) this.append(ev, "server");
    }
  }

  private randomScore(round: Round, court: number): Score {
    const game = round.games.find((g) => g.court === court);
    const rules = rulesFor(this.state.config, eventById(this.state.config, game?.eventId ?? null));
    let a = 0;
    let b = 0;
    const pA = 0.35 + this.rng.next() * 0.3;
    for (let i = 0; i < 60; i += 1) {
      if (this.rng.next() < pA) a += 1;
      else b += 1;
      if (isFinished(Math.max(a, b), Math.min(a, b), rules)) break;
    }
    return { a, b, capped: false };
  }

  /** One try at an event the rules may refuse (a limit, a closed window): refusals are fine here. */
  private attempt(event: TournamentEvent, by: string): void {
    try {
      this.append(event, by);
    } catch {
      /* the rules said no */
    }
  }

  /** The bench during a round: a heckle, some picks, every third round a curse, every fourth a twist. */
  private bench(round: Round): void {
    if (this.benchRound === round.no || pickProblem(this.state, round.no, this.now())) return;
    if (this.now() < round.startedAt + this.state.config.gameMs * 0.15) return;
    this.benchRound = round.no;
    const resting = round.lineup.sittingOut.filter((id) => (this.state.players[id]?.photo ?? 0) > 0);
    const courts = round.games.map((g) => g.court);
    const pick = <T,>(xs: T[]): T | undefined => xs[Math.floor(this.rng.next() * xs.length)];
    const heckler = pick(resting);
    const court = pick(courts);
    if (heckler && court) {
      this.attempt({ type: "heckle.sent", player: heckler, court, line: Math.floor(this.rng.next() * HECKLES.length) }, heckler);
    }
    for (const id of resting.filter((x) => x !== heckler).slice(0, 6)) {
      for (const on of courts) {
        if (this.rng.next() < 0.75) this.attempt({ type: "pick.made", player: id, roundNo: round.no, court: on, side: this.rng.next() < 0.5 ? "a" : "b" }, id);
      }
    }
    if (round.no % 3 === 1) {
      const thrower = resting.find((id) => (this.state.players[id]?.cursesLeft ?? 0) > 0);
      const on = pick(courts);
      // the three curses in turn (rounds 1, 4 and 7: wrong hand, no smashes, solo), so every one shows in pictures
      const kind = CURSE_KINDS[Math.floor(round.no / 3) % CURSE_KINDS.length]!;
      if (thrower && on) this.attempt({ type: "curse.thrown", player: thrower, court: on, side: this.rng.next() < 0.5 ? "a" : "b", kind }, thrower);
    }
    if (round.no % 4 === 2 && !round.twist && !round.eventIds.length) {
      const twists = this.state.config.eventTypes.filter((e) => e.kind === "handicap");
      const twist = pick(twists);
      if (twist) this.attempt({ type: "event.fired", roundNo: round.no, eventId: twist.id, courts: "all" }, "admin");
    }
  }


  /** Off the admin screen, somebody has to tap Start: once every court is ready, or a changeover after the clock. */
  private pretendAdminStarts(now: number): void {
    const s = this.state;
    if (s.clock.phase !== "idle") {
      this.idleSince = null;
      return;
    }
    this.idleSince ??= now;
    if (this.opts.identity.role === "admin" || s.config.autoAdvance || s.pausedAll || !s.draft || gamesDone(s)) return;
    const ready = courtsReady(s);
    if (ready && ready.waiting.length && now < this.idleSince + s.config.changeoverMs) return;
    this.attempt({ type: "round.started", lineup: s.draft }, "admin");
  }

  private tick(): void {
    const now = this.now();
    const alarm = nextAlarm(this.state);
    if (alarm !== null && now >= alarm) this.runAutopilot();
    // a forgotten score lands late, into the earlier round, while the next one is playing
    for (const past of this.state.rounds) {
      for (const game of past.games) {
        if (game.score || game.voided) continue;
        const key = `${past.no}:${game.court}`;
        const due = this.scoreAt.get(key);
        if (due === undefined || now < due || past.no === this.state.clock.roundNo) continue;
        if (this.opts.identity.role === "court" && game.court === this.opts.identity.court) continue;
        try {
          const score = { ...this.randomScore(past, game.court), capped: true };
          this.append({ type: "game.scored", roundNo: past.no, court: game.court, score }, `court-${game.court}`);
        } catch {
          this.scoreAt.set(key, Infinity);
        }
      }
    }
    const round = currentRound(this.state);
    if (round && this.state.clock.phase === "playing" && this.state.clock.roundNo === round.no) {
      this.bench(round);
      for (const game of round.games) {
        if (game.score || game.voided) continue;
        if (this.opts.identity.role === "court" && game.court === this.opts.identity.court) continue;
        const key = `${round.no}:${game.court}`;
        if (!this.scoreAt.has(key)) {
          this.scoreAt.set(key, round.startedAt + this.state.config.gameMs * (0.45 + this.rng.next() * 0.5));
          // every fourth round, court 3 forgets its score until the next round is under way, so the
          // owed-score nags and toasts can be seen in demo mode
          if (round.no % 4 === 2 && game.court === 3) {
            this.scoreAt.set(key, round.startedAt + this.state.config.gameMs * 1.4 + this.state.config.changeoverMs);
          }
        }
        if (now >= (this.scoreAt.get(key) ?? Infinity)) {
          try {
            this.append({ type: "game.scored", roundNo: round.no, court: game.court, score: this.randomScore(round, game.court) }, `court-${game.court}`);
          } catch {
            this.scoreAt.set(key, Infinity);
          }
        }
      }
      this.runAutopilot();
    }
    this.pretendAdminStarts(now);
    if (this.state.rounds.length === 6 && this.state.mode === "americano" && this.state.clock.phase === "changeover") {
      this.append({ type: "mode.switched", mode: "mexicano" }, "admin");
      this.append({ type: "config.updated", patch: { randomEvents: true, randomEventChance: 0.5 } }, "admin");
    }
  }
}
